import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import './rich-text-editor.css';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import { cn } from '@/core/lib/utils';
import { useEditorExtensions } from './hooks/useEditorExtensions';
import { useImagePaste } from './hooks/useImagePaste';
import { BubbleToolbar } from './components/BubbleToolbar';
import { FloatingToolbar } from './components/FloatingToolbar';
import { FixedToolbar } from './components/FixedToolbar';
import { SlashMenuHost } from './components/SlashMenuHost';
import { createSlashMenuStore } from './components/slash-menu-store';
import type { RichTextEditorProps } from './RichTextEditor.types';

function parseEditorContent(value?: string): object | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as object;
  } catch {
    return {
      type: 'doc',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: value }] }],
    };
  }
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  extensions: extraExtensions = [],
  className,
  readOnly = false,
  variant = 'plain',
  enableBubbleMenu = true,
  enableFloatingMenu = true,
  enableFixedToolbar = false,
  enableSlashCommands = true,
  enableDragHandle = true,
  enableMentions = false,
  enableCallouts = true,
  enableExpandable = true,
  enableCodeHighlighting = true,
  enableImages = false,
  enableVideoEmbed = false,
  onImageUpload,
  mentionFetchUsers,
}: RichTextEditorProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [slashStore] = useState(createSlashMenuStore);

  const extensions = useEditorExtensions({
    placeholder,
    enableSlashCommands,
    enableDragHandle,
    enableMentions,
    enableCallouts,
    enableExpandable,
    enableCodeHighlighting,
    enableImages,
    enableVideoEmbed,
    extensions: extraExtensions,
    mentionFetchUsers,
    slashMenuStore: enableSlashCommands ? slashStore : undefined,
  });

  const editor = useEditor({
    extensions,
    editable: !readOnly,
    content: parseEditorContent(value),
    onUpdate: ({ editor: e }) => {
      onChange?.(JSON.stringify(e.getJSON()));
    },
  });

  useEffect(() => {
    if (!editor) return;
    // `undefined` means the consumer doesn't control content externally — never
    // force-sync. An explicit empty string (e.g. after a comment is sent) must
    // still clear the editor, even though it's falsy.
    if (value === undefined) return;
    if (!value) {
      if (!editor.isEmpty) editor.commands.clearContent();
      return;
    }
    const currentJson = JSON.stringify(editor.getJSON());
    if (currentJson !== value) {
      const parsed = parseEditorContent(value);
      if (parsed) editor.commands.setContent(parsed);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useImagePaste(editor, onImageUpload);

  // Handle image file picked from slash command's native file picker
  useEffect(() => {
    if (!editor || !onImageUpload) return;
    const dom = editor.view.dom;
    const handler = (e: Event) => {
      const file = (e as CustomEvent<{ file: File }>).detail?.file;
      if (!file) return;
      void onImageUpload(file).then((url) => {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      });
    };
    dom.addEventListener('rich-text:image-file', handler);
    return () => dom.removeEventListener('rich-text:image-file', handler);
  }, [editor, onImageUpload]);

  const showFixedToolbar = enableFixedToolbar && !readOnly && !!editor;

  return (
    // `relative` wrapper anchors the slash menu. It must NOT clip, so the menu
    // (a sibling of the possibly `overflow-hidden` box) is never cut off, while
    // still living inside any scrollable Dialog for correct scroll behavior.
    <div ref={rootRef} className="relative">
      <div
        data-testid="rich-text-editor"
        data-variant={variant}
        className={cn(
          'flex flex-col text-sm',
          // `comment` owns a single bordered box with a focus ring — consumers must NOT wrap it.
          variant === 'comment' &&
            'overflow-hidden rounded-xl border border-input bg-background transition-shadow',
          variant === 'comment' &&
            !readOnly &&
            'focus-within:border-ring/70 focus-within:ring-[3px] focus-within:ring-ring/15',
          // `plain` embeds flush into the host layout (descriptions).
          variant === 'plain' && 'rounded-lg',
          readOnly && 'cursor-default',
          className,
        )}
      >
        {showFixedToolbar && <FixedToolbar editor={editor} variant={variant} />}

        {editor && enableBubbleMenu && !readOnly && (
          <BubbleMenu
            editor={editor}
            // @ts-expect-error tippyOptions is supported at runtime but missing from v3 types
            tippyOptions={{ duration: 100, placement: 'top' }}
            shouldShow={() => !editor.isActive('image') && !editor.state.selection.empty}
          >
            <BubbleToolbar editor={editor} />
          </BubbleMenu>
        )}

        {editor && enableFloatingMenu && !readOnly && (
          <FloatingMenu
            editor={editor}
            // @ts-expect-error tippyOptions is supported at runtime but missing from v3 types
            tippyOptions={{ duration: 100, placement: 'left' }}
          >
            <FloatingToolbar editor={editor} />
          </FloatingMenu>
        )}

        <EditorContent
          editor={editor}
          className={cn('flex-1', variant === 'comment' ? 'px-3 py-2.5' : 'py-1.5')}
        />
      </div>

      {enableSlashCommands && !readOnly && <SlashMenuHost store={slashStore} anchorRef={rootRef} />}
    </div>
  );
}
